import EditIcon from "@mui/icons-material/Edit";
import { Box, Button, Skeleton, Stack } from "@mui/material";
import { useHunterDetailQuery } from "Api/Queries/User/useHunterDetailQuery";
import { HunterPrerequisitesFormInitializer } from "Components/Hunter/HunterPrerequisitesForm/HunterPrerequisitesFormInitializer";
import { HunterDetailMerchants } from "Components/Hunter/HuntersOverview/HunterDetailMerchants";
import { HunterReadOnlyDetail } from "Components/Hunter/HuntersOverview/HunterReadOnlyDetail/HunterReadOnlyDetail";
import { MerchantsStatusWidgets } from "Components/Hunter/Merchants/MerchantsList/MerchantsStatusWidgets";
import { MerchantsStatusWidgetsSkeleton } from "Components/Hunter/Merchants/MerchantsList/MerchantsStatusWidgetsSkeleton";
import { AuthenticatedLayout } from "Components/Layout/AuthenticatedLayout";
import { ErrorText } from "Components/Shared/Errors/ErrorText";
import { PrimaryPaper } from "Components/Shared/PrimaryPaper";
import { StyledPageWrapper } from "Components/Shared/StyledComponents";
import { useCheckPermission } from "Hooks/usePermission";
import { Permission } from "Infrastructure/Api/Api";
import { AccessRightCodes } from "Models/AccessRightCodes";
import { Link } from "Navigation/Link";
import { Resources, useResource } from "Translations/Resources";
import { getAppUrl } from "Utils/UrlUtils";
import type { ReactNode } from "react";
import { useParams } from "react-router-dom";

const RESOURCES = Resources.Hunter.HuntersOverview;

export const HunterDetailPage = () => {
  const { id } = useParams();
  const { t } = useResource();
  const userID = id ? Number.parseInt(id, 10) : 0;
  const canUpdateHunters = useCheckPermission(
    AccessRightCodes.Hunters,
    Permission.Update,
  );
  const canUpdateParties = useCheckPermission(
    AccessRightCodes.Parties,
    Permission.Update,
  );
  const canUpdatePrerequisites = useCheckPermission(
    AccessRightCodes.HunterPrerequisites,
    Permission.Update,
  );
  const appUrl = getAppUrl();

  const { data, isLoading, isError } = useHunterDetailQuery(userID);

  if (isLoading) {
    return (
      <Layout>
        <MerchantsStatusWidgetsSkeleton />
        <PrimaryPaper>
          <Skeleton variant="rectangular" height={400} />
        </PrimaryPaper>
      </Layout>
    );
  }

  if (isError || !data) {
    return (
      <Layout>
        <PrimaryPaper>
          <ErrorText>{t(RESOURCES.LoadingError)}</ErrorText>
        </PrimaryPaper>
      </Layout>
    );
  }

  const title = data.party?.companyName || data.login;

  return (
    <Layout title={title}>
      <Stack gap={2}>
        {data.merchantStats && (
          <MerchantsStatusWidgets
            openRegistrations={data.merchantStats.openRegistrations}
            invitationsSent={data.merchantStats.invitationsSent}
            inOnboarding={data.merchantStats.inOnboarding}
            completed={data.merchantStats.activeMerchants}
          />
        )}
        <PrimaryPaper>
          <Box sx={{ position: "relative" }}>
            {(canUpdateHunters || canUpdateParties) && (
              <Button
                component={Link}
                to={`${appUrl("user-detail")}/${userID}`}
                variant="outlined"
                startIcon={<EditIcon />}
                sx={{ position: "absolute", top: 0, right: 0 }}
              >
                {t(RESOURCES.Detail.EditUser)}
              </Button>
            )}
            <HunterReadOnlyDetail hunter={data} />
          </Box>
        </PrimaryPaper>
        <HunterDetailMerchants userID={userID} />
        <HunterPrerequisitesFormInitializer
          userID={userID}
          isReadOnly={!canUpdatePrerequisites}
        />
      </Stack>
    </Layout>
  );
};

export default HunterDetailPage;

type LayoutProps = { children: ReactNode; title?: string };

const Layout = ({ children, title }: LayoutProps) => {
  const appUrl = getAppUrl();

  return (
    <AuthenticatedLayout
      title={title || ""}
      backNavigation={{
        mode: "historyStack",
        fallbackUrl: appUrl("hunters"),
      }}
    >
      <StyledPageWrapper maxWidth="unset !important">
        {children}
      </StyledPageWrapper>
    </AuthenticatedLayout>
  );
};
